import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { usePrefersReducedMotion, useFocusTrap } from "./useRightSheetA11y";
import RightSheetSection from "./RightSheetSection";
import RightSheetFooter from "./RightSheetFooter";
import { RightSheetLoading, RightSheetError, RightSheetEmpty } from "./RightSheetStates";
import Modal from "./Modal";

/**
 * RightSheet — the app's one standard drawer shell (portal-based).
 *
 * Anatomy: a sticky header, a single scrollable body, and an optional sticky
 * footer. Only the body scrolls. Consumers vary the content; the shell — width,
 * elevation, header, close control, footer — stays constant so every drawer in
 * the app reads as the same component.
 *
 * Features:
 *  - Portal to document.body
 *  - Slate-tinted backdrop with fade (200ms)
 *  - Panel slides from right (200ms ease-out), flush to the viewport edge
 *  - Left border + soft left-facing shadow (integrated, not a floating modal)
 *  - Three standardized widths via `size`: compact 400 / default 480 / wide 680
 *  - ESC to close, backdrop click to close
 *  - Body scroll lock while open
 *  - Always full-viewport: backdrop and panel cover the page header and sidebar
 *  - Optional eyebrow (contextual label above the title)
 *
 * Width is set via inline style, not Tailwind width utilities: this project
 * replaces theme.width with a tiny custom scale, so w-[Npx]/w-full are unreliable.
 */
const SHEET_WIDTHS = { compact: 400, default: 480, wide: 680 };

const OPEN_MS = 220;
const CLOSE_MS = 180;
const EASE_IN = "cubic-bezier(0.4, 0, 1, 1)";
const EASE_OUT = "cubic-bezier(0, 0, 0.2, 1)";

const RightSheet = ({
  open,
  onClose,
  title,
  subtitle,
  eyebrow,
  children,
  footer,
  size = "default",
  onBack,
  headerActions,
  loading = false,
  error = null,
  empty = null,
  onRetry,
  isDirty = false,
  dirtyPrompt,
  bodyKey,
}) => {
  const panelRef = useRef(null);
  // Per-instance ids. Two drawers can be open at once — BudgetCockpitPage
  // renders PeriodDrawer and BudgetScopeSheet as siblings with independent open
  // flags — and hard-coded ids made both dialogs resolve their accessible name
  // to whichever title happened to render first. Query the panel by
  // [data-right-sheet] rather than by id.
  const instanceId = useId();
  const titleId = `right-sheet-title-${instanceId}`;
  const subtitleId = `right-sheet-subtitle-${instanceId}`;
  const reducedMotion = usePrefersReducedMotion();
  const [mounted, setMounted] = useState(open);
  const [entered, setEntered] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Mount on open; keep the panel mounted for the exit transition on close.
  useEffect(() => {
    if (open) {
      setMounted(true);
      return undefined;
    }
    setEntered(false);
    const timer = setTimeout(() => setMounted(false), reducedMotion ? 0 : CLOSE_MS);
    return () => clearTimeout(timer);
  }, [open, reducedMotion]);

  // Enter transition — deliberately a SEPARATE effect keyed on `mounted`.
  //
  // Scheduling the enter alongside setMounted(true) above looks equivalent but
  // silently kills the open animation for every consumer that keeps RightSheet
  // rendered and merely toggles `open`: at that point the panel is not in the
  // DOM yet, so the rAF fires before the off-screen frame is ever painted and
  // the browser has only the end state to show. Consumers that unmount the
  // whole sheet when closed (so it re-mounts with open already true) happened
  // to animate correctly, which is why this looked page-specific.
  //
  // Keying on `mounted` runs this only after the panel has committed, and the
  // nested rAF guarantees the off-screen frame paints before we flip to
  // translate-x-0.
  useEffect(() => {
    if (!mounted || !open) return undefined;
    if (reducedMotion) {
      setEntered(true);
      return undefined;
    }
    let inner = null;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner !== null) cancelAnimationFrame(inner);
    };
  }, [mounted, open, reducedMotion]);

  useFocusTrap(panelRef, mounted, !confirmOpen);

  // Every dismissal path — ESC, backdrop click, the close button — routes
  // through here so an isDirty consumer gets one confirm gate instead of
  // three places that could silently discard edits.
  const requestClose = useCallback(() => {
    if (isDirty) {
      setConfirmOpen(true);
      return;
    }
    onClose();
  }, [isDirty, onClose]);

  // ESC key handler. Two guards:
  //  - `open`, not `mounted`: the panel outlives `open` by CLOSE_MS while it
  //    animates out, and a drawer that is already closing must not dismiss
  //    again (which would fire onClose twice, or re-open a dirty drawer's
  //    confirm on a drawer the user has already dismissed).
  //  - `!confirmOpen`: the confirm Modal registers its own document-level
  //    Escape handler, so without this a single press would open the confirm
  //    and immediately close it again.
  useEffect(() => {
    if (!mounted || !open) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !confirmOpen) {
        requestClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [mounted, open, confirmOpen, requestClose]);

  // Body scroll lock. Depends on confirmOpen too: Modal resets
  // document.body.style.overflow to "unset" on unmount, so cancelling the
  // confirm dialog would otherwise unlock page scroll behind a still-open
  // drawer. Re-running this effect re-asserts the lock.
  useEffect(() => {
    if (mounted) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mounted, confirmOpen]);

  const handleBackdropClick = (e) => {
    if (panelRef.current && !panelRef.current.contains(e.target)) {
      requestClose();
    }
  };

  const panelWidth = SHEET_WIDTHS[size] || SHEET_WIDTHS.default;

  let bodyContent = children;
  if (error) {
    bodyContent = <RightSheetError message={error} onRetry={onRetry} />;
  } else if (loading) {
    bodyContent = <RightSheetLoading />;
  } else if (empty) {
    bodyContent = <RightSheetEmpty empty={empty} />;
  }

  if (!mounted) return null;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop — slate-tinted so the underlying page stays legible (not a black
          scrim). pointer-events-none once `open` is false: the layer outlives the
          close by CLOSE_MS while it fades, and a full-viewport invisible scrim
          that still swallows clicks is worse than no animation at all. */}
      <div
        className={`fixed inset-0 z-40 transition-opacity ${
          entered ? "opacity-100" : "opacity-0"
        } ${open ? "" : "pointer-events-none"}`}
        style={{
          backgroundColor: "rgba(15, 23, 42, 0.22)",
          transitionDuration: reducedMotion ? "0ms" : `${CLOSE_MS}ms`,
        }}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Panel — flush to the right edge, left border + soft left-facing shadow */}
      <div
        ref={panelRef}
        className={`fixed inset-y-0 right-0 z-50 bg-card border-l border-border flex flex-col transition-transform ${
          entered ? "translate-x-0" : "translate-x-full"
        }`}
        style={{
          width: panelWidth,
          maxWidth: "100vw",
          boxShadow: "-12px 0 32px rgba(15, 23, 42, 0.10)",
          transitionDuration: reducedMotion ? "0ms" : `${open ? OPEN_MS : CLOSE_MS}ms`,
          transitionTimingFunction: open ? EASE_OUT : EASE_IN,
        }}
        data-right-sheet=""
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subtitle ? subtitleId : undefined}
        tabIndex={-1}
      >
        {/* Header — eyebrow / title / subtitle, optional back + actions, real close button */}
        {/* items-center, not items-start: it puts the close button on the title's
            optical line without a hardcoded offset, and keeps doing so whether or
            not this drawer passes an eyebrow or a subtitle. */}
        <div
          className="flex items-center justify-between gap-3 px-6 py-5 border-b border-border flex-shrink-0"
          style={{ minHeight: 72 }}
        >
          <div className="flex items-center gap-2 min-w-0">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="flex-shrink-0 -ml-1 inline-flex items-center justify-center rounded-sm border border-solid border-border box-border text-text-2 hover:bg-surface-2 hover:border-card-bd hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors cursor-pointer"
                style={{ width: 28, height: 28 }}
                aria-label="Back"
                title="Back"
              >
                <FontAwesomeIcon
                  icon={icon({ name: "chevron-left", style: "light" })}
                  className="text-[13px]"
                />
              </button>
            )}
            <div className="min-w-0">
              {eyebrow && (
                <div className="text-[11px] font-medium uppercase tracking-wide text-text-3 mb-1">
                  {eyebrow}
                </div>
              )}
              {title && (
                <h2
                  id={titleId}
                  className="text-lg font-semibold text-text leading-snug"
                >
                  {title}
                </h2>
              )}
              {subtitle && (
                <p id={subtitleId} className="text-sm text-text-2 mt-1.5">
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-start gap-1 flex-shrink-0">
            {headerActions}
            {/* A contained 28px control: light glyph inside a hairline box, so the
                dismissal reads as a discrete affordance without competing with the
                title. All three border properties are set explicitly — Toggle.css
                applies `button { all: unset }` globally, which resets width to
                `medium` (3px) and colour to currentColor, so `border-solid` alone
                would draw a heavy dark outline. */}
            <button
              type="button"
              onClick={requestClose}
              className="-mr-1 inline-flex items-center justify-center rounded-sm border border-solid border-border box-border text-text-2 hover:bg-surface-2 hover:border-card-bd hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors cursor-pointer"
              style={{ width: 28, height: 28 }}
              aria-label="Close"
              title="Close"
            >
              <FontAwesomeIcon
                icon={icon({ name: "xmark", style: "light" })}
                className="text-[13px]"
              />
            </button>
          </div>
        </div>

        {/* Body — the only scrollable region. `bodyKey` is opt-in: omitted, this
            div keeps its usual positional key like any other JSX node, so every
            existing caller that toggles `open` on a permanently-mounted
            RightSheet is unaffected. A caller that swaps between logically
            distinct "levels" of content in place (same JSX branch, so React
            would otherwise reuse this exact DOM node and its scrollTop) can
            pass a value that changes per level to force a fresh node — the
            browser then mounts it already scrolled to the top. */}
        <div
          key={bodyKey}
          data-testid="right-sheet-body"
          className="flex-1 overflow-y-auto px-6 py-6"
        >
          {bodyContent}
        </div>

        {/* Footer — sticky at bottom; consumers own the internal action layout.
            `empty:hidden` is what makes RightSheet.Footer's documented contract
            true: it returns null when given no actions, but `footer &&` only
            tests the ELEMENT's truthiness, so a read-only drawer would still get
            a bare 72px bordered bar. When the child renders nothing this wrapper
            has no child nodes, :empty matches, and the chrome collapses. */}
        {footer && (
          <div
            className="px-6 py-4 border-t border-border flex-shrink-0 bg-card empty:hidden"
            style={{ minHeight: 72 }}
          >
            {footer}
          </div>
        )}
      </div>

      {/* Confirm — gates every dismissal path when isDirty is set. Portals
          to document.body at z-[60], above the panel's z-50. */}
      <Modal
        show={confirmOpen}
        close={() => setConfirmOpen(false)}
        title={dirtyPrompt?.title || "Discard changes?"}
        description={dirtyPrompt?.description || "Your unsaved changes will be lost."}
        cancelButton="Cancel"
        confirmButton={dirtyPrompt?.confirmLabel || "Discard"}
        confirmAction={() => {
          setConfirmOpen(false);
          onClose();
        }}
        alertIcon
      />
    </>,
    document.body
  );
};

RightSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  eyebrow: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node,
  size: PropTypes.oneOf(["compact", "default", "wide"]),
  onBack: PropTypes.func,
  headerActions: PropTypes.node,
  loading: PropTypes.bool,
  error: PropTypes.node,
  empty: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
  onRetry: PropTypes.func,
  isDirty: PropTypes.bool,
  dirtyPrompt: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.node,
    confirmLabel: PropTypes.string,
  }),
  bodyKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

RightSheet.Section = RightSheetSection;
RightSheet.Footer = RightSheetFooter;

export default RightSheet;
